import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { X, PencilSimple } from "@phosphor-icons/react";
import useCatalog from "../hooks/useCatalog";

/**
 * Modal to edit document metadata.
 * Only available when document.approval_status is 'pendiente' or 'rechazado'.
 *
 * Props: { document, onClose, onSuccess }
 */
const GENRES = ["Fiscal", "Laboral", "Operativa", "Legal", "Ambiental", "Administrativa"];

export default function DocumentEditModal({ document: doc, onClose, onSuccess }) {
  const authorities = useCatalog("autoridad");
  const categories = useCatalog("categoria");
  const docTypes = useCatalog("tipo_documental");
  const [installations, setInstallations] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: doc.name || "",
    period: doc.period || new Date().getFullYear(),
    authority: doc.authority || "",
    genre: doc.genre || "",
    description: doc.description || "",
    installation_id: doc.installation_id || "",
    obligation_id: doc.obligation_id || "",
    category: doc.category || "",
    doc_type: doc.doc_type || "",
    emission_date: doc.emission_date || "",
    validity_date: doc.validity_date || "",
    expiration_date: doc.expiration_date || "",
    responsible: doc.responsible || "",
    observations: doc.observations || "",
  });

  useEffect(() => {
    api.get("/installations").then(r => setInstallations(r.data)).catch(() => {});
    api.get("/obligations").then(r => setObligations(r.data)).catch(() => {});
  }, []);

  const filteredObligations = obligations.filter(o => !f.installation_id || o.installation_id === f.installation_id);

  const save = async () => {
    if (!f.name) return toast.error("Nombre requerido");
    if (!f.authority) return toast.error("Autoridad requerida");
    setBusy(true);
    try {
      const payload = { ...f, period: Number(f.period) };
      // Empty strings → null for optional id fields
      ["installation_id", "obligation_id", "emission_date", "validity_date", "expiration_date"].forEach(k => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.put(`/documents/${doc.id}`, payload);
      toast.success("Documento actualizado");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="doc-edit-modal">
      <div className="bg-white rounded-md w-full max-w-2xl border border-neutral-200 max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">Edición</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <PencilSimple size={20} className="text-blue-600" weight="duotone" />
              Editar documento
            </h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="doc-edit-close">
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-800">
            Solo metadatos. Para reemplazar el archivo usa "Subir versión corregida" en el detalle.
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre</label>
            <input value={f.name} onChange={e=>setF({...f, name: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="edit-name"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Periodo (año)</label>
              <input type="number" value={f.period} onChange={e=>setF({...f, period: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="edit-period"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Responsable</label>
              <input value={f.responsible} onChange={e=>setF({...f, responsible: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="edit-responsible"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Autoridad</label>
              <select value={f.authority} onChange={e=>setF({...f, authority: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="edit-authority">
                <option value="">Seleccionar...</option>
                {authorities.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Género</label>
              <select value={f.genre} onChange={e=>setF({...f, genre: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="edit-genre">
                <option value="">Seleccionar...</option>
                {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Categoría</label>
              <select value={f.category} onChange={e=>setF({...f, category: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="edit-category">
                <option value="">Seleccionar...</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Tipo documental</label>
              <select value={f.doc_type} onChange={e=>setF({...f, doc_type: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="edit-doctype">
                <option value="">Seleccionar...</option>
                {docTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Emisión</label>
              <input type="date" value={f.emission_date || ""} onChange={e=>setF({...f, emission_date: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="edit-emission"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Vigencia</label>
              <input type="date" value={f.validity_date || ""} onChange={e=>setF({...f, validity_date: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="edit-validity"/>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Vencimiento</label>
              <input type="date" value={f.expiration_date || ""} onChange={e=>setF({...f, expiration_date: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="edit-expiration"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Instalación</label>
              <select value={f.installation_id} onChange={e=>setF({...f, installation_id: e.target.value, obligation_id: ""})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="edit-installation">
                <option value="">— Ninguna —</option>
                {installations.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Obligación</label>
              <select value={f.obligation_id} onChange={e=>setF({...f, obligation_id: e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="edit-obligation">
                <option value="">— Ninguna —</option>
                {filteredObligations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={f.description} onChange={e=>setF({...f, description: e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md" data-testid="edit-description"/>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Observaciones</label>
            <textarea value={f.observations} onChange={e=>setF({...f, observations: e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md" data-testid="edit-observations"/>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:bg-white">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="edit-submit">
            {busy ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
